/**
 * Shared browser-Back coordinator for the SPA's nested detail views (#231).
 *
 * No router here, so a hardware/browser Back press has to be told what "one
 * step back" means by hand. Multiple independent views can be nested at once
 * (result slideout -> full-page Benefits Explorer; a VSP plan-table popup on
 * top of the VSP slideout) — a single shared module-level stack, with exactly
 * ONE `popstate` listener, is what makes that safe. If every nested view
 * registered its OWN `popstate` listener instead, one physical Back press
 * would fire ALL of them at once (browser events fan out to every listener,
 * there's no "innermost wins" built in) and tear down more than one level per
 * press. Routing every level through this single stack means a pop always
 * actions exactly the most-recently-opened level.
 *
 * Each open view pushes one history entry (via `useBrowserBackSentinel`) and
 * registers the callback that undoes JUST that view. Closing a view in-app
 * (not via browser Back) removes it from the in-memory stack AND reclaims its
 * history slot with `history.back()` — otherwise every in-app close would
 * strand one stamped entry, and each stranded entry would swallow one later
 * Back press (they'd accumulate: k open/close cycles = k dead presses, on the
 * Explorer's own Back button, the common exit path — exactly the "Back does
 * nothing" complaint #231 is about). The `history.back()` DOES re-fire
 * `popstate`, and that's safe by the same reconciliation that handles every
 * other pop: the entry is already gone from the in-memory stack, so the
 * traversal lands on the level below with nothing above it to close — a
 * no-op, not a recursive teardown.
 *
 * Known limitation (documented per #231's investigate-first allowance):
 * `popstate` fires identically for Back and Forward — there is no direction
 * signal on the event. Every pushed entry stamps `{ vrBackId, vrBackSession }`
 * into its history state, so `handlePop` reconciles against `event.state`
 * instead of blindly popping the in-memory stack's top: it closes every stack
 * entry whose id is greater than the id of the entry actually landed on (an
 * unstamped/foreign state, e.g. the base entry, closes everything). That
 * makes a single `popstate` correct no matter how many real history entries
 * it spans — a normal one-level Back, a multi-entry jump via a long-pressed
 * Back button (`history.go(-n)` still fires exactly one `popstate` for the
 * whole traversal), or Forward: landing back on a deeper entry than the
 * in-memory stack's current top has nothing above it to close, so it's a
 * clean no-op rather than tearing down the live view underneath it.
 *
 * Residual cost of that Forward no-op: it leaves the history position sitting
 * ABOVE the live top, so the next Back press is spent traveling back down to
 * the live level (nothing closes on that press). Forward is the one remaining
 * way a press can appear dead — in-app closes no longer strand entries (see
 * `popBackEntry`), and unlike them Forward doesn't accumulate: a second
 * Forward press has no entry to land on at all.
 */

type BackHandler = () => void;

interface StackEntry {
  id: number;
  onBack: BackHandler;
}

/**
 * Ids restart at 1 on every page load, but the stamps already written into
 * session history OUTLIVE the load that wrote them — a reload throws away this
 * module's state, not the browser's history. So an id on its own can't tell a
 * live stamp from one left behind by an earlier load, and the two id spaces
 * are not comparable. Every stamp therefore also records WHICH page load wrote
 * it, and `handlePop` only trusts an id from this one.
 */
function newSessionId(): string {
  const c = globalThis.crypto;
  if (typeof c?.randomUUID === 'function') return c.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

let stack: StackEntry[] = [];
let nextId = 1;
let session = newSessionId();
let listenerAttached = false;

function handlePop(event: PopStateEvent) {
  const state = event.state as { vrBackId?: unknown; vrBackSession?: unknown } | null;
  // A stamp from any OTHER page load is treated exactly like an unstamped one
  // — depth 0, so everything unwinds. Comparing its id against ours would be
  // meaningless, and the failure it produces is silent: after an F5, a
  // leftover stamp carrying the same number as a live one reads as "already
  // at this level", nothing closes, and the Back press just dies (#231).
  const sameSession = state?.vrBackSession === session;
  const arrivedId = sameSession && typeof state?.vrBackId === 'number' ? state.vrBackId : 0;

  // Close every entry deeper than the one actually landed on, innermost first.
  while (stack.length > 0 && stack[stack.length - 1].id > arrivedId) {
    const entry = stack.pop()!;
    entry.onBack();
  }
}

function ensureListener() {
  if (listenerAttached) return;
  listenerAttached = true;
  window.addEventListener('popstate', handlePop);
}

/** Push one history sentinel entry and register what closes just this view. */
export function pushBackEntry(onBack: BackHandler): number {
  ensureListener();
  const id = nextId++;
  stack.push({ id, onBack });
  window.history.pushState({ vrBackId: id, vrBackSession: session }, '');
  return id;
}

/**
 * Remove an entry after its view closed WITHOUT a browser Back press.
 *
 * With `reclaimSlot`, also consumes the entry's now-orphaned history slot via
 * `history.back()`, so the close doesn't leave a stamp behind to swallow a
 * later Back press. Only the hook's in-app-close path asks for this; the
 * unmount path must NOT (StrictMode's dev-only simulated unmount would turn
 * the reclaim into a navigation that races the immediate re-push).
 *
 * Two guards keep the reclaim from ever navigating when history didn't
 * actually strand a slot:
 * - id not in the stack ⇒ this close was CAUSED by a popstate (`handlePop`
 *   already removed the entry and the browser already moved) — navigating
 *   again would double-step.
 * - id present but not innermost ⇒ a deeper view is still open above it, so
 *   the current history position isn't this entry's slot; there's nothing
 *   safe to reclaim (shouldn't happen with properly nested views).
 */
export function popBackEntry(id: number, opts?: { reclaimSlot?: boolean }) {
  const idx = stack.findIndex((e) => e.id === id);
  if (idx === -1) return;
  const wasInnermost = idx === stack.length - 1;
  stack.splice(idx, 1);
  if (opts?.reclaimSlot && wasInnermost) {
    window.history.back();
  }
}

/**
 * Test-only: reset module state. Equivalent to a fresh page load — including a
 * new session id — so tests can simulate a reload (module state gone, browser
 * history intact) rather than only a clean start.
 */
export function __resetBackStackForTests() {
  stack = [];
  nextId = 1;
  session = newSessionId();
  if (listenerAttached) {
    window.removeEventListener('popstate', handlePop);
    listenerAttached = false;
  }
}

// A hot update re-evaluates this module but leaves the OUTGOING copy's
// listener attached to `window`, so without this every Back press in dev would
// be handled twice — once per module instance — against two separate stacks.
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    window.removeEventListener('popstate', handlePop);
    listenerAttached = false;
  });
}
